import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Download, Users, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-MX', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function downloadCSV(rows) {
  const header = ['Nombre', 'Email', 'Fecha de registro'];
  const body = rows.map(r => [r.name || '', r.email || '', r.first_seen_at || '']);
  const escape = (v) => `"${String(v).replace(/"/g, '""')}"`;
  const csv = [header, ...body].map(r => r.map(escape).join(',')).join('\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const today = new Date().toISOString().slice(0, 10);
  a.href = url;
  a.download = `leads-vitamina-${today}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function AdminLeadsDialog({ open, onOpenChange }) {
  const { token, isAdmin } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  const load = async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/admin/leads`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLeads(data.leads || []);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Error al cargar leads.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && isAdmin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isAdmin]);

  const filtered = query.trim()
    ? leads.filter(l =>
        (l.email || '').toLowerCase().includes(query.toLowerCase()) ||
        (l.name || '').toLowerCase().includes(query.toLowerCase())
      )
    : leads;

  const handleExport = () => {
    if (filtered.length === 0) {
      toast.info('No hay leads para exportar.');
      return;
    }
    downloadCSV(filtered);
    toast.success(`CSV exportado: ${filtered.length} lead${filtered.length === 1 ? '' : 's'}.`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] flex flex-col" data-testid="admin-leads-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-blue-100">
              <Users className="h-5 w-5 text-[#1A56DB]" />
            </div>
            <div>
              <DialogTitle className="text-xl">Leads capturados</DialogTitle>
              <DialogDescription className="text-xs mt-1">
                Correos corporativos verificados por OTP.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center mt-2">
          <Input
            placeholder="Buscar por email o nombre…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-10 flex-1"
            data-testid="admin-leads-search"
          />
          <Button
            onClick={load}
            variant="outline"
            size="sm"
            className="gap-2 h-10"
            disabled={loading}
            data-testid="admin-leads-refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          <Button
            onClick={handleExport}
            size="sm"
            className="gap-2 h-10 bg-[#1A56DB] text-white hover:bg-[#1548c2]"
            disabled={loading || filtered.length === 0}
            data-testid="admin-leads-export-csv"
          >
            <Download className="h-4 w-4" />
            Exportar CSV
          </Button>
        </div>

        {/* Content */}
        <div className="mt-3 flex-1 overflow-hidden flex flex-col">
          <div className="text-xs text-slate-500 mb-2">
            {loading ? 'Cargando…' : `${filtered.length} de ${leads.length} leads`}
          </div>

          {error && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 mb-2" data-testid="admin-leads-error">
              {error}
            </div>
          )}

          <div className="flex-1 overflow-y-auto border border-slate-200 rounded-xl">
            <table className="w-full text-sm" data-testid="admin-leads-table">
              <thead className="bg-slate-50 sticky top-0">
                <tr className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  <th className="px-4 py-3">Nombre</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3 whitespace-nowrap">Fecha de registro</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && leads.length === 0 ? (
                  <tr><td colSpan={3} className="px-4 py-8 text-center text-slate-400">
                    <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Cargando leads…
                  </td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={3} className="px-4 py-8 text-center text-slate-400">
                    {query ? 'Sin coincidencias para tu búsqueda.' : 'Aún no hay leads capturados.'}
                  </td></tr>
                ) : filtered.map((l) => (
                  <tr key={l.id || l.email} className="hover:bg-slate-50 transition-colors" data-testid="admin-lead-row">
                    <td className="px-4 py-3 text-slate-900 font-medium">{l.name || '—'}</td>
                    <td className="px-4 py-3 text-slate-700 font-mono text-xs">{l.email}</td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{formatDate(l.first_seen_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
